import { useCallback, useMemo, useReducer, useRef } from 'react';
import gsap from 'gsap';

function reducer(state, action) {
  switch (action.type) {
    case 'setState':
      return {
        ...state,
        progress: action.progress,
        currentScene: action.currentScene,
        toScene: action.toScene,
        transition: action.transition
      };
    case 'updateProgress':
      return {
        ...state,
        progress: action.progress
      };
    case 'setCurrentScene':
      return {
        ...state,
        currentScene: action.currentScene
      };
    case 'setToScene':
      return {
        ...state,
        toScene: action.toScene
      };
    case 'setTransition':
      return {
        ...state,
        transition: action.transition
      };
    default:
      throw new Error();
  }
}

// 场景控制器
const useSceneController = params => {
  /**
   * initialScene: 初始场景
   * initialProgress: 初始进程
   * SCENES: 场景列表
   * transition: 场景切换效果
   */
  const { initialScene, initialProgress = 1, SCENES, transition } = params || {};
  // console.log('transition:',transition);

  const tweenRef = useRef(null);
  /**
   * 控制器状态
   */
  const [state, dispatchStateEvent] = useReducer(reducer, {
    progress: initialProgress,
    currentScene: initialScene,
    transition,
    toScene: initialScene
  });

  /**
   * 更新动画进度
   * @type {(function(*): void)|*}
   */
  const updateProgress = useCallback(newProgress => {
    // console.log(newProgress);
    dispatchStateEvent({
      type: 'updateProgress',
      progress: newProgress
    });
  }, []);

  /**
   * 切换当前场景
   * @type {(function(*): void)|*}
   */
  const setCurrentScene = useCallback(newCurrentScene => {
    dispatchStateEvent({
      type: 'setCurrentScene',
      currentScene: newCurrentScene
    });
  }, []);

  const setToScene = useCallback(newToScene => {
    dispatchStateEvent({
      type: 'setToScene',
      toScene: newToScene
    });
  }, []);

  const setTransition = useCallback(newTransition => {
    dispatchStateEvent({
      type: 'setTransition',
      transition: newTransition
    });
  }, []);

  /**
   * 切换到某个场景
   * @type {(function(*, *): void)|*}
   */
  const goToScene = useCallback((newScene, gsapTweenParams = {}, transitions) => {
    const { onUpdate, onComplete, duration = 2, ...restGsapTweenParams } = gsapTweenParams || {};
    const newToScene = typeof newScene === 'string' ? (SCENES || {})[newScene] : newScene;

    tweenRef.current && tweenRef.current.pause();
    tweenRef.current && tweenRef.current.progress(1);
    tweenRef.current && tweenRef.current.kill();
    updateProgress(0);

    // console.log('prepare');
    // console.timeLog('scene');

    if (!newToScene) {
      throw new Error('找不到对应场景');
    }

    setToScene(newToScene);
    transitions && setTransition(transitions);

    let sceneState = {
      progress: 0
    };
    tweenRef.current = gsap.to(sceneState, {
      progress: 1,
      duration: duration,
      onUpdate: () => {
        tweenRef.current && tweenRef.current.play();
        updateProgress(sceneState.progress);
        onUpdate && onUpdate();
        // console.log('update');
      },
      onComplete: () => {
        setCurrentScene(newToScene);
        onComplete && onComplete();
        // console.log('end');
        // console.timeEnd('scene');
      },
      ...(restGsapTweenParams || {})
    });
  }, []);

  const transitionParams = useMemo(
    () => {
      return {
        from: state.currentScene?.src,
        to: state.toScene?.src,
        progress: state.progress,
        transition: state.transition
      };
    },
    [state]
  );

  return {
    ...state,
    transitionParams,
    goToScene
  };
};

export default useSceneController;
